import { useEffect, useState } from 'react';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import SaveIcon from '@mui/icons-material/Save';
import { CircularProgress, Tooltip } from '@mui/material';
import DatePicker from '@components/DatePicker';
import { openPopUp } from '@containers/App/containers/PopUp/actions';
import POPUP_TYPES from '@containers/App/containers/PopUp/types';
import { updateUserBirthdate } from '@containers/Admin/containers/UserPage/actions';
import { useTranslation } from 'react-i18next';
import { EditorFields, EditorSaveAction, EditorSaveButton } from '../../styles';

const parseBirthdate = (value?: string): Date | null => {
  if (!value) return null;
  const date = /^\d+$/.test(value) ? new Date(Number(value)) : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatBirthdate = (value: Date | null) => {
  if (!value) return '';
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const EditBirthdate = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const userData = useSelector((state: RootStateOrAny) => state.getIn(['userPage', 'userData']));
  const saving = useSelector((state: RootStateOrAny) => state.getIn(['userPage', 'loading']));
  const [birthdate, setBirthdate] = useState<Date | null>(null);

  useEffect(() => setBirthdate(parseBirthdate(userData?.birthdate)), [userData?.birthdate]);

  const birthdateValue = formatBirthdate(birthdate);
  const savedBirthdateValue = formatBirthdate(parseBirthdate(userData?.birthdate));
  const disabled = Boolean(saving) || !birthdateValue || birthdateValue === savedBirthdateValue;
  const userName = `${userData?.firstName || ''} ${userData?.lastName || ''}`.trim();

  const confirmUpdate = () => {
    if (disabled || !userData?.id) return;
    dispatch(openPopUp(POPUP_TYPES.CONFIRM, {
      onConfirm: (confirm: boolean) => confirm && dispatch(updateUserBirthdate(userData.id, birthdateValue)),
      titleText: t('Admin.User.birthdateChangeTitle'),
      bodyText: t('Admin.User.confirmBirthdateUpdate', { userName }),
      confirmText: t('Actions.Simple.yes'),
      cancelText: t('Actions.Simple.no'),
      disableBackdrop: true,
    }));
  };

  return (
    <EditorFields>
      <DatePicker
        value={birthdate}
        onChange={setBirthdate}
        format="DD/MM/YYYY"
        views={['year', 'month', 'day']}
        disableFuture
        disabled={Boolean(saving)}
        slotProps={{
          textField: {
            size: 'small',
            sx: {
              '& .MuiInputAdornment-root .MuiSvgIcon-root': {
                fontSize: 18,
              },
            },
            onKeyDown: (event) => {
              if (event.key === 'Enter') confirmUpdate();
            },
          },
        }}
      />
      <Tooltip title={t('Actions.Instance.save')} arrow>
        <EditorSaveAction>
          <EditorSaveButton
            type="button"
            aria-label={t('Actions.Instance.save')}
            disabled={disabled}
            onClick={confirmUpdate}
          >
            {saving ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
          </EditorSaveButton>
        </EditorSaveAction>
      </Tooltip>
    </EditorFields>
  );
};